/**
 * Default MVP projection scheme for the event framework.
 *
 * A [com.oasisfeng.invaluable.data.LinkProjector] turns graph-free Link structure into stable, content-addressed
 * wire identities. This adapter ships one concrete scheme (`invaluable.projection.sha256.v1`) so the event layer has
 * a real, collision-resistant identity to persist and replicate.
 *
 * Like [Ed25519Credential], this lives outside the model module: the model only knows that a projection scheme
 * is named by a protocol Link and renders compact payloads. SHA-256 is one replaceable algorithm behind that boundary.
 */

package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.Dimensions
import com.oasisfeng.invaluable.Link
import com.oasisfeng.invaluable.NestedLink
import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.LinkProjectionScheme
import com.oasisfeng.invaluable.data.LinkProjector
import com.oasisfeng.invaluable.data.Stream.OctetStream
import com.oasisfeng.invaluable.identity.Codec
import com.oasisfeng.invaluable.then
import kotlinx.io.bytestring.ByteString

/**
 * SHA-256 link projector: `projection = sha256(version || endpoint(parent) || endpoint(target))`.
 *
 * Each endpoint is length-prefixed and tagged so that a present endpoint can never be confused with the kernel root
 * (`null`) and parent/target can never be transposed. Because the scheme only consumes the parent and target
 * projections (never issuer, amount, or any local handle), two structurally equivalent Links always project equally,
 * and the recursive `(parent, target)` recurrence makes the projection of any Link content-address its full structure.
 */
class Sha256LinkProjector(schemeName: String = SCHEME_NAME) : LinkProjector {

    override val scheme: LinkProjectionScheme = LinkProjectionScheme.of(schemeProtocol(schemeName))

    /**
     * Memo of the `(parent, target) -> projection` recurrence.
     *
     * The recurrence is what makes a projection content-address a Link's *whole* structure, so computing one head costs
     * a digest per link beneath it — and a post body is one link per bit (`OctetStream`), i.e. thousands. Nothing
     * in the pipeline computes a given head once: `project` re-walks from scratch on every call, `resolveLink` re-derives
     * every record it passes to prove the store did not lie, and a batch is applied twice (validate, then apply). A real
     * dog-fooding store of 64 commitments took ~1.9M digests to boot, which is where linkscape's ten-second live start
     * went. Since the recurrence is a pure function of its two endpoint payloads, memoizing it is observationally
     * transparent: it removes recomputation, never a check — every call still returns the digest it would have computed.
     *
     * Growth is bounded by the number of distinct links this process has projected, which is the same order as the
     * (also process-lifetime) Link interner this sits above; it carries the same "revisit before production" caveat.
     */
    private val memo = HashMap<Pair<LinkProjection?, LinkProjection?>, LinkProjection>()

    /** Whole-Link memo (see [LinkProjector.projections]): the recurrence memo above still leaves `project` re-walking. */
    override val projections: MutableMap<Link, LinkProjection> = HashMap()

    override fun projectLink(parent: LinkProjection?, target: LinkProjection?): LinkProjection {
        require(parent == null || parent.scheme == scheme) { "Parent projection scheme does not match projector scheme." }
        require(target == null || target.scheme == scheme) { "Target projection scheme does not match projector scheme." }
        return memo.getOrPut(parent to target) { digest(parent?.payload, target?.payload) }
    }

    private fun digest(parent: ByteString?, target: ByteString?): LinkProjection {
        val message = ByteArray(1 + endpointSize(parent) + endpointSize(target))
        message[0] = VERSION_TAG
        val afterParent = message.writeEndpoint(1, parent)
        message.writeEndpoint(afterParent, target)
        return LinkProjection(scheme, ByteString(*Codec.sha256(message)))
    }

    private fun endpointSize(payload: ByteString?) = if (payload == null) 1 else 1 + 4 + payload.size

    /** Tag (root/present) + 4-byte big-endian length + payload, so concatenation stays unambiguous. */
    private fun ByteArray.writeEndpoint(at: Int, payload: ByteString?): Int {
        if (payload == null) { this[at] = ROOT_TAG; return at + 1 }
        this[at] = PRESENT_TAG
        val size = payload.size
        this[at + 1] = (size ushr 24).toByte()
        this[at + 2] = (size ushr 16).toByte()
        this[at + 3] = (size ushr 8).toByte()
        this[at + 4] = size.toByte()
        payload.copyInto(this, at + 5)
        return at + 5 + size
    }

    companion object {
        // v2: Milestone 9 moved commitment content (target + quantity) off the spine behind a content digest, changing
        // every commitment projection. The scheme bump makes the break explicit; old persisted/replicated data is reset.
        const val SCHEME_NAME: String = "invaluable.projection.sha256.v2"

        private const val VERSION_TAG: Byte = 0x01
        private const val ROOT_TAG: Byte = 0x00
        private const val PRESENT_TAG: Byte = 0x01

        /** Scheme discriminator Link, mirroring the text-protocol shape used by other projection schemes. */
        private fun schemeProtocol(name: String): NestedLink = Dimensions.MEANING().then(OctetStream.encode(name.encodeToByteArray()).link)
    }
}
